import { useState, useEffect, useMemo } from "react";
import { Shuffle, ListOrdered, ShieldCheck, Link2, Loader2, ArrowLeft, Save, Plus, X, FileText, ArrowUpRight } from "lucide-react";
import { validateSafeUrl } from "./interstitial/interstitialConfig";
import MultiBotSelector from "../../components/BotSelector/MultiBotSelector";
import { useNotification } from "../../components/Notifications/NotificationContext";
import { SELECTED_CLS, UNSELECTED_CLS } from "../../utils/selectionStyles";

const BLOCK_METHODS = [
    { id: "safepage", label: "Página segura", Icon: FileText, desc: "Exibe uma página neutra hospedada pela Spectrum." },
    { id: "redirect", label: "Redirecionar", Icon: ArrowUpRight, desc: "Envia o acesso bloqueado para outra URL." },
];

const EMPTY_FORM = { slug: "", selectedBots: [], routingMode: "random", hasCloaker: false, blockMethod: "safepage", safeUrl: "" };

const ROUTING_MODES = [
    { id: "random", label: "Aleatório", Icon: Shuffle, desc: "Distribui aleatoriamente os cliques entre todos os bots ativos." },
    { id: "sequential", label: "Sequencial", Icon: ListOrdered, desc: "Usa o primeiro bot ativo; se ele cair, passa para o próximo." },
];

export default function RedirectForm({ open = true, bots, onSubmit, onCancel, initialData, isEditing = false, ownBaseUrl = "" }) {
    const { showError } = useNotification();
    const [loading, setLoading] = useState(false);

    const sortedBots = useMemo(() => (Array.isArray(bots) ? [...bots] : []), [bots]);

    const [form, setForm] = useState(EMPTY_FORM);
    const [safeUrlError, setSafeUrlError] = useState("");

    useEffect(() => {
        if (initialData) {
            setForm({
                slug: initialData.slug,
                selectedBots: initialData.bot_ids || [],
                routingMode: initialData.routing_mode || "random",
                hasCloaker: !!(initialData.cloaker_param || initialData.has_cloaker),
                blockMethod: initialData.block_method === "redirect" ? "redirect" : "safepage",
                safeUrl: initialData.safe_url || "",
            });
        } else {
            setForm(EMPTY_FORM);
        }
        setSafeUrlError("");
    }, [initialData]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.slug.trim()) { showError("Slug é obrigatório."); return; }
        if (form.selectedBots.length === 0) { showError("Selecione pelo menos um bot."); return; }
        if (form.hasCloaker && form.blockMethod === "redirect") {
            const err = validateSafeUrl(form.safeUrl, ownBaseUrl);
            setSafeUrlError(err || "");
            if (err) return;
        }
        setLoading(true);
        try {
            await onSubmit(form);
        } finally {
            setLoading(false);
        }
    };

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-[99999] flex items-center justify-center p-2 md:p-4">
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onCancel} />
            <div
                className="relative w-full max-w-2xl rounded-2xl border border-white/[0.09] bg-[#10141a] p-4 md:p-6 shadow-[0_24px_64px_rgba(0,0,0,0.55)] text-white"
                style={{ maxHeight: "92vh", overflowY: "auto", colorScheme: "dark" }}
            >
                {/* Header */}
                <div className="flex items-start justify-between mb-6 gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                        <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-[rgba(139,92,246,0.12)] border border-[rgba(139,92,246,0.18)] flex items-center justify-center text-[#a78bfa]">
                            <Link2 size={20} />
                        </div>
                        <div className="min-w-0">
                            <h2 className="text-lg font-semibold">{isEditing ? "Editar Redirecionador" : "Novo Redirecionador"}</h2>
                            <p className="text-sm text-white/50">Crie um slug que redireciona para seus bots e marca vendas.</p>
                        </div>
                    </div>
                    <button onClick={onCancel} className="action-btn flex-shrink-0" aria-label="Fechar">
                        <X size={18} />
                    </button>
                </div>

                <form
                    onSubmit={handleSubmit}
                    className="space-y-6"
                    style={{ opacity: loading ? 0.6 : 1, pointerEvents: loading ? "none" : "auto" }}
                >
                    {/* Cloaker */}
                    <button
                        type="button"
                        onClick={() => setForm((f) => ({ ...f, hasCloaker: !f.hasCloaker }))}
                        className={`w-full flex items-center gap-3 p-4 rounded-xl border text-left transition-all ${form.hasCloaker
                            ? "bg-[#4c1d95] border-[#6d28d9]"
                            : "bg-white/[0.02] border-white/[0.06] hover:border-white/[0.1] hover:bg-white/[0.04]"}`}
                    >
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${form.hasCloaker ? "bg-white/10 text-white" : "bg-white/[0.04] text-white/40"}`}>
                            <ShieldCheck size={20} />
                        </div>
                        <div className="min-w-0 flex-1">
                            <div className="text-sm font-semibold text-white">Usar Cloaker</div>
                            <div className="text-xs text-white/50">
                                Gera um parâmetro <code className="text-[#fbbf24]">sx</code> e protege o funil de acessos sem anúncio.
                            </div>
                        </div>
                        <span className={`relative w-10 h-6 rounded-full border transition-colors flex-shrink-0 ${form.hasCloaker ? "bg-[var(--brand-500)] border-[var(--brand-400)]" : "bg-white/10 border-white/20"}`}>
                            <span className={`absolute top-[3px] w-4 h-4 rounded-full bg-white shadow-sm transition-all ${form.hasCloaker ? "left-[19px]" : "left-[3px]"}`} />
                        </span>
                    </button>

                    {/* Resposta para tráfego bloqueado (só com cloaker) */}
                    {form.hasCloaker && (
                        <div className="border-t border-white/[0.065] pt-5 -mt-1">
                            <fieldset aria-describedby="redirect-safe-url-error">
                                <legend className="block text-sm font-semibold text-white mb-2">Resposta para tráfego bloqueado</legend>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                    {BLOCK_METHODS.map(({ id, label, Icon, desc }) => {
                                        const active = form.blockMethod === id;
                                        return (
                                            <label
                                                key={id}
                                                className={`flex items-start gap-3 p-3.5 rounded-xl border text-left transition-all cursor-pointer focus-within:ring-2 focus-within:ring-[rgba(167,139,250,0.7)] ${active
                                                    ? SELECTED_CLS
                                                    : UNSELECTED_CLS}`}
                                            >
                                                <input
                                                    className="sr-only"
                                                    type="radio"
                                                    name="redirect-block-method"
                                                    value={id}
                                                    checked={active}
                                                    onChange={() => { setForm((f) => ({ ...f, blockMethod: id })); setSafeUrlError(""); }}
                                                />
                                                <span aria-hidden="true" className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${active ? "bg-white/10 text-white" : "bg-white/[0.04] text-white/40"}`}>
                                                    <Icon size={18} />
                                                </span>
                                                <span className="min-w-0">
                                                    <span className="block text-sm font-semibold text-white">{label}</span>
                                                    <span className="block text-[11px] text-white/50 leading-snug">{desc}</span>
                                                </span>
                                            </label>
                                        );
                                    })}
                                </div>
                            </fieldset>

                            {form.blockMethod === "redirect" && (
                                <div className="mt-4">
                                    <label htmlFor="redirect-safe-url" className="block">
                                        <span className="block text-sm font-medium text-white/85">URL segura</span>
                                        <span className="mt-1 block text-xs leading-5 text-white/50">
                                            Use uma página pública que possa receber visitantes bloqueados. Vamos conferir se ela está no ar antes de salvar.
                                        </span>
                                    </label>
                                    <input
                                        id="redirect-safe-url"
                                        type="url"
                                        inputMode="url"
                                        autoComplete="url"
                                        placeholder="https://exemplo.com/pagina-segura"
                                        value={form.safeUrl}
                                        onChange={(e) => { setForm((f) => ({ ...f, safeUrl: e.target.value })); setSafeUrlError(""); }}
                                        aria-invalid={!!safeUrlError}
                                        aria-describedby="redirect-safe-url-error"
                                        className={`mt-2 flex h-12 w-full rounded-xl border bg-white/[0.02] px-4 text-base md:text-sm text-white placeholder:text-white/20 outline-none transition-all hover:border-white/10 focus:ring-1 focus:ring-[rgba(139,92,246,0.3)] focus:border-[rgba(139,92,246,0.3)] ${safeUrlError ? "border-[#f87171]/60" : "border-white/[0.06]"}`}
                                    />
                                    {safeUrlError && (
                                        <p id="redirect-safe-url-error" className="mt-1.5 text-xs text-[#f87171]">{safeUrlError}</p>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Bots */}
                    <div>
                        <label className="block text-sm font-semibold text-white mb-2">
                            Selecionar Bots <span className="text-[var(--brand-400)]">*</span>
                        </label>
                        {sortedBots.length === 0 ? (
                            <p className="text-white/40 text-sm">Você ainda não possui bots cadastrados.</p>
                        ) : (
                            <MultiBotSelector
                                bots={sortedBots}
                                selected={form.selectedBots}
                                onChange={(ids) => setForm((prev) => ({ ...prev, selectedBots: ids }))}
                            />
                        )}
                    </div>

                    {/* Modo de roteamento */}
                    <div>
                        <label className="block text-sm font-semibold text-white mb-2">
                            Modo de Roteamento <span className="text-[var(--brand-400)]">*</span>
                        </label>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {ROUTING_MODES.map(({ id, label, Icon, desc }) => {
                                const active = form.routingMode === id;
                                return (
                                    <button
                                        key={id}
                                        type="button"
                                        onClick={() => setForm((f) => ({ ...f, routingMode: id }))}
                                        className={`flex items-start gap-3 p-3.5 rounded-xl border text-left transition-all ${active
                                            ? SELECTED_CLS
                                            : UNSELECTED_CLS}`}
                                    >
                                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${active ? "bg-white/10 text-white" : "bg-white/[0.04] text-white/40"}`}>
                                            <Icon size={18} />
                                        </div>
                                        <div className="min-w-0">
                                            <div className="text-sm font-semibold text-white">{label}</div>
                                            <div className="text-[11px] text-white/50 leading-snug">{desc}</div>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Slug */}
                    <div>
                        <label className="block text-sm font-semibold text-white mb-2">
                            Slug <span className="text-[var(--brand-400)]">*</span>
                        </label>
                        <div className={`spt-input-wrap flex items-center rounded-xl border overflow-hidden transition-colors ${isEditing ? "opacity-60 border-white/[0.06]" : "border-white/[0.08]"} bg-white/[0.02]`}>
                            <span className="flex items-center gap-1.5 px-3 py-2.5 text-sm text-white/40 border-r border-white/[0.06] flex-shrink-0">
                                <Link2 size={14} /> /r/
                            </span>
                            <input
                                className="flex-1 bg-transparent px-3 py-2.5 text-sm text-white border-none outline-none placeholder:text-white/30"
                                placeholder="meu-slug-unico"
                                value={form.slug}
                                onChange={(e) => setForm({ ...form, slug: e.target.value })}
                                disabled={isEditing}
                                required
                                style={{ cursor: isEditing ? "not-allowed" : "text", outline: "none", boxShadow: "none" }}
                            />
                        </div>
                        <small className="block mt-1.5 text-xs text-white/40">
                            {isEditing ? "Slug não pode ser alterado." : "Alfanuméricos, underscore (_) ou hífen (-) apenas."}
                        </small>
                    </div>

                    {/* Ações */}
                    <div className="flex gap-3 justify-end pt-2">
                        <button type="button" onClick={onCancel} className="btn-secondary flex items-center gap-2">
                            <ArrowLeft size={15} /> Voltar
                        </button>
                        <button disabled={loading} className="btn-primary flex items-center gap-2">
                            {loading ? (
                                <><Loader2 size={15} className="animate-spin" /> {isEditing ? "Atualizando..." : "Criando..."}</>
                            ) : (
                                <>{isEditing ? <Save size={15} /> : <Plus size={15} />} {isEditing ? "Atualizar" : "Criar"}</>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
